import React, { useRef } from 'react';
import { Card, Button, Row, Col, Badge, ListGroup } from 'react-bootstrap';
import { PostureScanAPIResponse, PostureScanUserData } from '../types';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import html2pdf from 'html2pdf.js';

interface ScanResultsProps {
  data: PostureScanAPIResponse;
  userData: PostureScanUserData | null;
  onBackToUpload: () => void;
  onNewScan: () => void;
}

const ScanResults: React.FC<ScanResultsProps> = ({
  data,
  userData,
  onBackToUpload,
  onNewScan,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const handleDownloadPDF = async () => {
    const element = printRef.current;
    if (!element) return;

    // Get user name for filename
    const userName = userData?.userName || 'User';
    
    // Parse timestamp correctly (handle Unix timestamp in seconds)
    let dateStr = '';
    try {
      const timestampValue = data.timestamp;
      let scanDate: Date | null = null;
      
      if (timestampValue) {
        if (typeof timestampValue === 'number') {
          // If it's a timestamp in seconds (unix timestamp), convert to milliseconds
          scanDate = new Date(timestampValue > 9999999999 ? timestampValue : timestampValue * 1000);
        } else if (typeof timestampValue === 'string') {
          // Try parsing as number first, then as date string
          const numTimestamp = parseInt(timestampValue);
          if (!isNaN(numTimestamp)) {
            scanDate = new Date(numTimestamp > 9999999999 ? numTimestamp : numTimestamp * 1000);
          } else {
            scanDate = new Date(timestampValue);
          }
        }
      }
      
      // Check if date is valid, otherwise use current date
      if (scanDate && !isNaN(scanDate.getTime())) {
        dateStr = scanDate.toISOString().split('T')[0];
      } else {
        dateStr = new Date().toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Error parsing scan date:', error);
      dateStr = new Date().toISOString().split('T')[0];
    }
    
    const filename = `Posture_Scan_Report_${userName.replace(/\s+/g, '_')}_${dateStr}.pdf`;

    // Clone the element to avoid modifying the original
    const clonedElement = element.cloneNode(true) as HTMLElement;
    
    // Hide buttons in the cloned element
    const buttons = clonedElement.querySelectorAll('.hide-on-print');
    buttons.forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });

    // PDF options optimized for readability
    const opt = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: { 
        unit: 'mm' as const, 
        format: 'a4' as const, 
        orientation: 'portrait' as const,
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'] as const,
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-inside-avoid'
      }
    };

    try {
      // Show loading indicator
      const button = document.querySelector('.download-pdf-btn') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Generating PDF...';
      }

      console.log('Starting PDF generation...');
      
      // Create the PDF worker
      const pdfWorker = html2pdf().set(opt).from(clonedElement);
      
      // Try direct save first (most compatible)
      await pdfWorker.save();
      
      console.log('PDF download initiated successfully');

      // Reset button
      if (button) {
        setTimeout(() => {
          button.disabled = false;
          button.innerHTML = '<i class="bi bi-download me-2"></i>Download Report as PDF';
        }, 1000);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      
      // Reset button on error
      const button = document.querySelector('.download-pdf-btn') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = '<i class="bi bi-download me-2"></i>Download Report as PDF';
      }
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const renderScoreCard = (title: string, score: number, icon: string) => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="text-center">
        <div className="mb-3">
          <KTSVG path={icon} className="svg-icon-3x svg-icon-primary" />
        </div>
        <h5 className="text-muted mb-2">{title}</h5>
        <div className="mb-2">
          <h1 className={`display-4 fw-bold text-${getScoreColor(score)} mb-0`}>
            {score}
          </h1>
          <small className="text-muted">/ 100</small>
        </div>
        <Badge bg={getScoreColor(score)} className="px-3 py-2">
          {getScoreLabel(score)}
        </Badge>
      </Card.Body>
    </Card>
  );

  const renderAnalysisList = (items: any[], title: string, variant: string = 'primary') => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-4">
        <h5 className="mb-3 d-flex align-items-center">
          <KTSVG path="/media/icons/duotune/general/gen048.svg" className={`svg-icon-2 svg-icon-${variant} me-2`} />
          {title}
        </h5>
        <ListGroup variant="flush">
          {items.map((item, index) => (
            <ListGroup.Item key={index} className="border-start border-3 border-primary mb-2 bg-light">
              {typeof item === 'string' ? (
                <div>{item}</div>
              ) : (
                <div>
                  {Object.entries(item).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))}
                </div>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    );
  };

  const renderImprovementsList = (items: any[], title: string) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-4">
        <h5 className="mb-3 d-flex align-items-center">
          <KTSVG path="/media/icons/duotune/general/gen043.svg" className="svg-icon-2 svg-icon-success me-2" />
          {title}
        </h5>
        <ListGroup variant="flush">
          {items.map((item, index) => (
            <ListGroup.Item key={index} className="border-start border-3 border-success mb-2 bg-light-success">
              {typeof item === 'string' ? (
                <div className="d-flex align-items-start">
                  <span className="badge bg-success me-2 mt-1">{index + 1}</span>
                  <span>{item}</span>
                </div>
              ) : (
                <div>
                  {Object.entries(item).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))}
                </div>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    );
  };

  return (
    <div className="posture-scan-results" ref={printRef}>
      {/* Header Card */}
      <Card className="mb-4 border-0 shadow-lg">
        <Card.Body className="text-center py-5 bg-gradient-primary text-white" 
          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div className="mb-3">
            <KTSVG path="/media/icons/duotune/general/gen043.svg" className="svg-icon-4x text-white" />
          </div>
          <h2 className="mb-2" style={{ color: 'white' }}>Posture Scan Complete!</h2>
          <p className="mb-0" style={{ color: 'white' }}>Your posture analysis results are ready</p>
          {userData && (
            <div className="mt-3">
              <small className="opacity-75" style={{ color: 'white' }}>{userData.userName}</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Score Summary Cards */}
      <Row className="mb-4">
        <Col md={6} className="mb-3 mb-md-0">
          {renderScoreCard(
            'Front View Score',
            data.front_view.score,
            '/media/icons/duotune/general/gen049.svg'
          )}
        </Col>
        <Col md={6}>
          {renderScoreCard(
            'Side View Score',
            data.side_view.score,
            '/media/icons/duotune/general/gen049.svg'
          )}
        </Col>
      </Row>

      {/* Front View Analysis */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom py-3">
          <h4 className="mb-0 d-flex align-items-center">
            <KTSVG path="/media/icons/duotune/arrows/arr027.svg" className="svg-icon-2 svg-icon-primary me-2" />
            Front View Analysis
          </h4>
        </Card.Header>
        <Card.Body>
          {renderAnalysisList(data.front_view.analysis, 'Analysis Points', 'primary')}
          {renderImprovementsList(data.front_view.improvements, 'Recommended Improvements')}
        </Card.Body>
      </Card>

      {/* Side View Analysis */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom py-3">
          <h4 className="mb-0 d-flex align-items-center">
            <KTSVG path="/media/icons/duotune/arrows/arr028.svg" className="svg-icon-2 svg-icon-info me-2" />
            Side View Analysis
          </h4>
        </Card.Header>
        <Card.Body>
          {renderAnalysisList(data.side_view.analysis, 'Analysis Points', 'info')}
          {renderImprovementsList(data.side_view.improvements, 'Recommended Improvements')}
        </Card.Body>
      </Card>

      {/* Metadata */}
      <Card className="mb-4 border-0 shadow-sm bg-light">
        <Card.Body>
          <Row>
            <Col md={6}>
              <small className="text-muted d-block mb-1">Request ID</small>
              <code className="small">{data.request_id}</code>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block mb-1">Timestamp</small>
              <div className="small">
                {(() => {
                  try {
                    const timestampValue = data.timestamp;
                    let scanDate: Date | null = null;
                    
                    if (timestampValue) {
                      if (typeof timestampValue === 'number') {
                        // If it's a timestamp in seconds (unix timestamp), convert to milliseconds
                        scanDate = new Date(timestampValue > 9999999999 ? timestampValue : timestampValue * 1000);
                      } else if (typeof timestampValue === 'string') {
                        // Try parsing as number first, then as date string
                        const numTimestamp = parseInt(timestampValue);
                        if (!isNaN(numTimestamp)) {
                          scanDate = new Date(numTimestamp > 9999999999 ? numTimestamp : numTimestamp * 1000);
                        } else {
                          scanDate = new Date(timestampValue);
                        }
                      }
                    }
                    
                    // Check if date is valid
                    if (scanDate && !isNaN(scanDate.getTime())) {
                      return scanDate.toLocaleString();
                    } else {
                      return 'N/A';
                    }
                  } catch (error) {
                    console.warn('Error parsing timestamp:', error);
                    return 'N/A';
                  }
                })()}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <Card className="mb-4 border-0 shadow-sm hide-on-print">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Button
                variant="success"
                className="w-100 py-3 download-pdf-btn"
                onClick={handleDownloadPDF}
              >
                <i className="bi bi-download me-2"></i>
                Download Report as PDF
              </Button>
            </Col>
            <Col md={4}>
              <Button
                variant="outline-primary"
                className="w-100 py-3"
                onClick={onBackToUpload}
              >
                <KTSVG path="/media/icons/duotune/arrows/arr021.svg" className="svg-icon-3 me-2" />
                Back to Upload
              </Button>
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                className="w-100 py-3"
                onClick={onNewScan}
              >
                <KTSVG path="/media/icons/duotune/general/gen055.svg" className="svg-icon-3 me-2" />
                Start New Scan
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Info Footer */}
      <Card className="border-0 bg-light-primary">
        <Card.Body className="text-center">
          <p className="mb-0 text-muted small">
            <KTSVG path="/media/icons/duotune/general/gen044.svg" className="svg-icon-4 me-2" />
            This analysis is powered by AI and should be used as a guide. For medical concerns, please consult a healthcare professional.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ScanResults;
